/**
 * Settings > Two-step verification policy (ADMIN; tracker item 63): which
 * staff roles must use an authenticator app. Users of a required role who
 * have not set it up can still sign in, but only reach the setup page until
 * they do. Off for every role by default. Requiring a role needs the server's
 * secret key (503 MFA_UNAVAILABLE otherwise).
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { createAPIClient, IMfaApi } from '../../api';
import { ACCOUNT_SECURITY_PATH, MfaEnforceableRole, MfaPolicy } from '../../api/mfaTypes';
import { getErrorMessage } from '../../utils/apiError';
import { canManageMfaPolicy } from '../../utils/permissions';
import { primaryButtonClass } from '../../components/formStyles';
import { Toast } from '../../components/Toast';

const ROLE_TEXT: Record<MfaEnforceableRole, { label: string; hint: string }> = {
  ADMIN: { label: 'Admins', hint: 'Recommended: admins can change users, money settings and the general ledger.' },
  OFFICE_MANAGER: { label: 'Office managers', hint: 'Recommended: office staff see customers, invoices and payments.' },
  TECHNICIAN: { label: 'Technicians', hint: 'Optional: each technician needs an authenticator app on their phone.' },
};

export function MfaPolicySettingsPage() {
  const { user } = useAuth();
  const api = React.useMemo<IMfaApi>(() => createAPIClient(), []);
  const allowed = canManageMfaPolicy(user);
  const [policy, setPolicy] = useState<MfaPolicy | null>(null);
  const [selected, setSelected] = useState<MfaEnforceableRole[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!allowed) return;
    (async () => {
      try {
        const loaded = await api.getMfaPolicy();
        setPolicy(loaded);
        setSelected(loaded.requiredRoles);
      } catch (err) {
        setError(getErrorMessage(err, 'Could not load the two-step verification policy'));
      }
    })();
  }, [api, allowed]);

  if (!allowed) {
    return <p className="p-4 text-sm text-gray-600">Only admins can change the two-step verification policy.</p>;
  }

  const toggle = (role: MfaEnforceableRole) =>
    setSelected((prev) => (prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]));

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const saved = await api.updateMfaPolicy(selected);
      setPolicy(saved);
      setSelected(saved.requiredRoles);
      setNotice('Two-step verification policy saved');
    } catch (err) {
      setError(getErrorMessage(err, 'Could not save the policy'));
    } finally {
      setSaving(false);
    }
  };

  const changed = policy !== null && [...selected].sort().join(',') !== [...policy.requiredRoles].sort().join(',');

  return (
    <main className="p-4 sm:p-6 max-w-2xl space-y-4">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Two-step verification policy</h1>
        <p className="mt-1 text-sm text-gray-600">
          Require an authenticator app code at sign-in for these roles. People in a required role who have not set it up are asked to do so the next time they use the app, and cannot do anything else until they have. Customer portal logins are not affected.
        </p>
        <p className="mt-1 text-sm text-gray-600">
          Set up your own on the <Link to={ACCOUNT_SECURITY_PATH} className="text-blue-600 hover:underline">security page</Link> first. A user who lost their phone can be reset from Settings &gt; Users.
        </p>
      </div>
      {error && <Toast kind="error">{error}</Toast>}
      {notice && <Toast kind="success">{notice}</Toast>}
      {policy && !policy.available && (
        <p className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          Two-step verification is not available: the server has no APP_SECRET_KEY. No role can be required until it is set.
        </p>
      )}
      {policy && (
        <form onSubmit={save} className="space-y-3">
          <fieldset className="space-y-2">
            <legend className="text-sm font-medium text-gray-700">Required for</legend>
            {policy.enforceableRoles.map((role) => (
              <label key={role} className="flex items-start gap-3 rounded-md border border-gray-200 p-3">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4"
                  checked={selected.includes(role)}
                  onChange={() => toggle(role)}
                  disabled={saving}
                />
                <span>
                  <span className="block text-sm font-semibold text-gray-900">{ROLE_TEXT[role].label}</span>
                  <span className="block text-xs text-gray-600">{ROLE_TEXT[role].hint}</span>
                </span>
              </label>
            ))}
          </fieldset>
          <button type="submit" disabled={saving || !changed} className={primaryButtonClass}>
            {saving ? 'Saving...' : 'Save'}
          </button>
        </form>
      )}
    </main>
  );
}
